import React from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "@mui/material";

const KeywordHighlighter = ({ content, keywords = [], onKeywordClick }) => {
  // If no keywords, just render the markdown
  if (!keywords || keywords.length === 0) {
    return <ReactMarkdown>{content}</ReactMarkdown>;
  }

  // Sort keywords by length (descending) to match longer phrases first
  const sortedKeywords = [...keywords].sort((a, b) => b.length - a.length);

  // Create a regex pattern for all keywords (escaping special characters)
  const escapedKeywords = sortedKeywords.map((keyword) =>
    keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const pattern = `(${escapedKeywords.join("|")})`;
  const regex = new RegExp(pattern, "gi");

  // Custom components for ReactMarkdown
  const components = {
    p: ({ children }) => {
      if (!children) return <p>{children}</p>;

      // Process each text node within paragraphs
      return (
        <p>
          {React.Children.map(children, (child) => {
            if (typeof child !== "string") return child;

            // Process the text to highlight keywords
            const parts = [];
            let lastIndex = 0;
            let match;

            // Reset regex for each search
            regex.lastIndex = 0;

            while ((match = regex.exec(child)) !== null) {
              // Add text before the match
              if (match.index > lastIndex) {
                parts.push(child.substring(lastIndex, match.index));
              }

              // Add the highlighted keyword
              const keyword = match[0];
              parts.push(
                <Link
                  key={match.index}
                  component="button"
                  onClick={() => onKeywordClick(keyword)}
                  sx={{
                    textDecoration: "none",
                    borderBottom: "1px dashed",
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: "medium",
                    display: "inline",
                    "&:hover": {
                      textDecoration: "none",
                      opacity: 0.8,
                    },
                  }}
                >
                  {keyword}
                </Link>
              );

              lastIndex = regex.lastIndex;
            }

            // Add remaining text
            if (lastIndex < child.length) {
              parts.push(child.substring(lastIndex));
            }

            return parts.length > 0 ? parts : child;
          })}
        </p>
      );
    },

    // Add similar processing for other text-containing elements
    li: ({ children }) => {
      if (!children) return <li>{children}</li>;

      return (
        <li>
          {React.Children.map(children, (child) => {
            if (typeof child !== "string") return child;

            const parts = [];
            let lastIndex = 0;
            let match;

            regex.lastIndex = 0;

            while ((match = regex.exec(child)) !== null) {
              if (match.index > lastIndex) {
                parts.push(child.substring(lastIndex, match.index));
              }

              const keyword = match[0];
              parts.push(
                <Link
                  key={match.index}
                  component="button"
                  onClick={() => onKeywordClick(keyword)}
                  sx={{
                    textDecoration: "none",
                    borderBottom: "1px dashed",
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: "medium",
                    display: "inline",
                    "&:hover": {
                      textDecoration: "none",
                      opacity: 0.8,
                    },
                  }}
                >
                  {keyword}
                </Link>
              );

              lastIndex = regex.lastIndex;
            }

            if (lastIndex < child.length) {
              parts.push(child.substring(lastIndex));
            }

            return parts.length > 0 ? parts : child;
          })}
        </li>
      );
    },

    // Handle headings
    h1: ({ children }) => processHeading(children, "h1"),
    h2: ({ children }) => processHeading(children, "h2"),
    h3: ({ children }) => processHeading(children, "h3"),
    h4: ({ children }) => processHeading(children, "h4"),
    h5: ({ children }) => processHeading(children, "h5"),
    h6: ({ children }) => processHeading(children, "h6"),
  };

  // Helper function to process headings
  const processHeading = (children, Tag) => {
    if (!children) return React.createElement(Tag, {}, children);

    return React.createElement(
      Tag,
      {},
      React.Children.map(children, (child) => {
        if (typeof child !== "string") return child;

        const parts = [];
        let lastIndex = 0;
        let match;

        regex.lastIndex = 0;

        while ((match = regex.exec(child)) !== null) {
          if (match.index > lastIndex) {
            parts.push(child.substring(lastIndex, match.index));
          }

          const keyword = match[0];
          parts.push(
            <Link
              key={match.index}
              component="button"
              onClick={() => onKeywordClick(keyword)}
              sx={{
                textDecoration: "none",
                borderBottom: "1px dashed",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: "medium",
                display: "inline",
                "&:hover": {
                  textDecoration: "none",
                  opacity: 0.8,
                },
              }}
            >
              {keyword}
            </Link>
          );

          lastIndex = regex.lastIndex;
        }

        if (lastIndex < child.length) {
          parts.push(child.substring(lastIndex));
        }

        return parts.length > 0 ? parts : child;
      })
    );
  };

  return <ReactMarkdown components={components}>{content}</ReactMarkdown>;
};

export default KeywordHighlighter;
